import {analyzeCandidateCv,cvAnalysisError} from "../../utils/analyzeCandidateCv";
import CandidateStars from "../../components/CandidateStars";
import ClientMultiSelect from "./ClientMultiSelect";
import {rqthDocumentForm} from "../../utils/rqthDocument";
import {regionArray} from "../../utils/geographicSectors";
import PostalCityField from "../../components/PostalCityField";
import {usePostalCities} from "../../hooks/usePostalCities";
import { freshModal } from "../../components/freshModal";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../../components/reusableComponents/Input";
import { Label } from "../../components/reusableComponents/Label";
import qualifications from "../../utils/qualifications";
import { message, Select } from "antd";
import { countries } from "../../utils/countries";
import { normalizeSearch } from "../../utils/listSearch";
import axios from "../../axios/AxiosInstance";
import { User, Mail, Phone, Upload, X, Building, Award, FileText, Plus, Check, MapPin, Calendar, Shield, Users, Heart } from "lucide-react";


const birthDepartmentOptions = [
  ...regionArray.filter(label=>!label.startsWith("98 - ")).map(label=>({value:label.split(" - ")[0],label})),
  {value:"99",label:"99 — Naissance à l’étranger"},
];

function CreateCandidateModalFormSession({
  open,
  onOpenChange,
  candidates,
  setCandidates,
}) {
  const clientSearchTimer = useRef(null);
  const clientSearchAbort = useRef(null);
  const clientSearchVersion = useRef(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cvAnalyzing,setCvAnalyzing] = useState(false);
  const [cvAnalysisMessage,setCvAnalysisMessage] = useState('');
  const cvAnalysisVersion = useRef(0);
  useEffect(()=>()=>{cvAnalysisVersion.current++;},[]);
  const analyzeCV = async file => {
    const version=++cvAnalysisVersion.current;
    setCvAnalyzing(true);setCvAnalysisMessage('');
    try {
      const data=await analyzeCandidateCv(file);
      if(version!==cvAnalysisVersion.current)return;
      const detected=(data.qualifications||[]).filter(value=>qualifications.includes(value));
      setFormData(current=>({...current,qualifications:[...new Set([...current.qualifications,...detected])]}));
      setCvAnalysisMessage(detected.length ? `${detected.length} métier(s) détecté(s). Vérifiez les qualifications ajoutées ci-dessous.` : 'Aucun métier du catalogue détecté. Vous pouvez choisir les qualifications ci-dessous.');
    } catch(error) {
      if(version===cvAnalysisVersion.current)setCvAnalysisMessage(cvAnalysisError(error));
    } finally {if(version===cvAnalysisVersion.current)setCvAnalyzing(false);}
  };
  const [rqthFile,setRqthFile] = useState(null);
  const [rqthStart,setRqthStart] = useState("");
  const [rqthEnd,setRqthEnd] = useState("");
  const [rqthUnlimited,setRqthUnlimited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clients, setClients] = useState([]);


  const inputFileRef = useRef(null);

  // Constantes pour l'optimisation
  const MAX_VISIBLE_CLIENTS = 50; // Limite d'affichage pour éviter les lags
  const SEARCH_DEBOUNCE_MS = 300;

  const [formData, setFormData] = useState({
    // Informations personnelles
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    phone2: "",
    address: "",
    postCode: "",
    town: "",
    dob: "",
    nationality: "",
    birthCountry: "",
    birthTown: "",
    birthDepartment: "",
    securityNumber: "",

    // Contact d'urgence
    urgentContactName: "",
    urgentContactPhone: "",

    // Informations professionnelles
    qualifications: [], // Pour l'API (array)
    status: "Disponible",

    // Compétences et certifications
    licences: [],
    diplomas: [],
    habilitations: [],
    caceses: [],
    epis: [],
    locomotions: [],

    // Préférences
    availabilities: [],
    zones: [],
    monthlySalaryExpectations: "",
    yearlySalaryExpectations: "",

    // Autres
    rating: 0,
    isRQTH: false,
    comment: "",
    clients: [],
  });

  // Clients filtrés et optimisés avec useMemo
  const postal = usePostalCities({postCode:formData.postCode,town:formData.town,enabled:open,onTownChange:town=>setFormData(current=>({...current,town}))});

  const filteredClients = useMemo(() => {
    if (!clientSearch || clientSearch.length < 3) return [];
    return clients.slice(0, MAX_VISIBLE_CLIENTS); // Limite pour éviter les lags
  }, [clients, clientSearch]);

  const searchClient = useCallback(word => {
    const version=++clientSearchVersion.current;
    clearTimeout(clientSearchTimer.current);
    clientSearchAbort.current?.abort();
    setClientSearch(word);setIsLoadingClient(word.trim().length >= 3);setClients([]);
    if(word.trim().length<3)return;
    clientSearchTimer.current=setTimeout(async()=>{
      setIsLoadingClient(true);
      const controller = new AbortController();
      clientSearchAbort.current = controller;
      try {
        const {data}=await axios.post("client/freelance/find-by-text",{query:word},{signal:controller.signal});
        if(version===clientSearchVersion.current)setClients(Array.isArray(data)?data:[]);
      }catch{
        if(!controller.signal.aborted && version===clientSearchVersion.current)message.error("Impossible de rechercher les clients.");
      }finally{if(version===clientSearchVersion.current)setIsLoadingClient(false);}
    },SEARCH_DEBOUNCE_MS);
  },[]);
  useEffect(()=>()=>{clearTimeout(clientSearchTimer.current);clientSearchAbort.current?.abort();clientSearchVersion.current++;},[]);



  const addCandidate = (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    if (!isFormComplete()) {
      message.error("Veuillez remplir tous les champs!");
      setLoading(false);
      return;
    }

    if(formData.isRQTH) {
      try {rqthDocumentForm({file:rqthFile,startDate:rqthStart,endDate:rqthEnd,unlimited:rqthUnlimited});}
      catch(error){message.error(error.message);setLoading(false);return;}
    }
    const newPhone = formData.phone.replaceAll(" ", "").replaceAll(".", "");

    // Préparation des qualifications pour l'API
    const qualificationsArray = [...new Set(formData.qualifications || [])];

    // Création de l'objet FormData
    const formPayload = new FormData();
    if(formData.rating)formPayload.append("rating",String(formData.rating));

    // Informations de base (obligatoires)
    formPayload.append("firstname", formData.firstname);
    formPayload.append("lastname", formData.lastname);
    formPayload.append("email", formData.email);
    formPayload.append("phone", newPhone);

    // Qualifications (format attendu par l'API)
    qualificationsArray.forEach(value => formPayload.append("qualifications[]", value));

    // Clients sélectionnés
    if (formData.clients && formData.clients.length > 0) {
      formData.clients.forEach(client=>formPayload.append("clients[]", client._id));
    }

    // Informations complémentaires (optionnelles)
    if (formData.phone2) formPayload.append("phone2", formData.phone2);
    if (formData.address) formPayload.append("address", formData.address);
    if (formData.postCode) formPayload.append("postCode", formData.postCode);
    if (formData.town) formPayload.append("town", formData.town);
    if (formData.dob) formPayload.append("dob", formData.dob);
    if (formData.nationality) formPayload.append("nationality", formData.nationality);
    if (formData.birthCountry) formPayload.append("birthCountry", formData.birthCountry);
    if (formData.birthTown) formPayload.append("birthTown", formData.birthTown);
    if (formData.birthDepartment) formPayload.append("birthDepartment", formData.birthDepartment);
    if (formData.securityNumber) formPayload.append("securityNumber", formData.securityNumber);
    if (formData.urgentContactName) formPayload.append("urgentContactName", formData.urgentContactName);
    if (formData.urgentContactPhone) formPayload.append("urgentContactPhone", formData.urgentContactPhone);
    if (formData.status) formPayload.append("status", formData.status);
    if (formData.comment) formPayload.append("comment", formData.comment);

    // Booléens
    formPayload.append("isRQTH", formData.isRQTH.toString());

    // fichier
    if (selectedFile) {
      formPayload.append("file", selectedFile); // 'file' doit correspondre à ce que le backend attend
    }



    axios
      .post("tempworker-candidate", formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(async (res) => {
        let created = res.data;
        if(formData.isRQTH) {
          try {
            await axios.post("document",rqthDocumentForm({file:rqthFile,candidateId:created._id,startDate:rqthStart,endDate:rqthEnd,unlimited:rqthUnlimited}));
            created={...created,isRQTH:true};
          }catch{
            message.warning("Candidat créé. Le justificatif RQTH n’a pas été enregistré : ajoutez-le dans les documents de sa fiche.");
          }
        }
        setCandidates(current=>[...current.filter(row=>row._id!==created._id),created]);
        setLoading(false);
        onOpenChange(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.response?.data?.message || "Impossible d’enregistrer le candidat et ses clients pressentis. Vos saisies sont conservées.");
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type === "application/pdf" && file.size <= 5 * 1024 * 1024) {
      setSelectedFile(event.target.files[0]);
      analyzeCV(file);
    } else {
      message.error("Veuillez ajouter un CV PDF de 5 Mo maximum.");
    }
  };

  const handleClick = () => {
    inputFileRef.current.click();
  };

  const isFormComplete = () => {
    const { firstname, lastname, email, phone } = formData;
    return firstname !== "" && lastname !== "" && email !== "" && phone !== "";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => onOpenChange(false)}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header avec gradient moderne */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <motion.div
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <User className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      className="text-2xl font-bold"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                    >
                      Nouveau candidat
                    </motion.h2>
                    <motion.p
                      className="text-blue-100 text-sm"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                    >
                      Créez un nouveau profil candidat
                    </motion.p>
                  </div>
                </div>
                <motion.button
                  onClick={() => onOpenChange(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all duration-200"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
            </div>

            {/* Contenu du formulaire */}
            <div className="flex-1 overflow-y-auto max-h-[calc(90vh-120px)]">
              <form onSubmit={addCandidate} className="p-8 space-y-6">
                {/* Section Informations personnelles */}
                <motion.div
                  className="space-y-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-blue-100 rounded-lg p-2">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Informations personnelles</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Prénom */}
                    <div className="space-y-2">
                      <Label htmlFor="firstname" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Prénom</span>
                      </Label>
                      <Input
                        id="firstname"
                        value={formData.firstname}
                        onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                        placeholder="Prénom du candidat"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Nom */}
                    <div className="space-y-2">
                      <Label htmlFor="lastname" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Nom</span>
                      </Label>
                      <Input
                        id="lastname"
                        value={formData.lastname}
                        onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                        placeholder="Nom du candidat"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span>Email</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@exemple.com"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Téléphone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>Téléphone</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Téléphone 2 */}
                    <div className="space-y-2">
                      <Label htmlFor="phone2" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>Téléphone 2 (optionnel)</span>
                      </Label>
                      <Input
                        id="phone2"
                        type="tel"
                        value={formData.phone2}
                        onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Date de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="dob" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>Date de naissance</span>
                      </Label>
                      <Input
                        id="dob"
                        type="date"
                        value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Nationalité */}
                    <div className="space-y-2">
                      <Label htmlFor="nationality" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span>Nationalité</span>
                      </Label>
                      <Select
                        id="nationality"
                        aria-label="Nationalité"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        value={formData.nationality || undefined}
                        options={countries.map(value => ({value,label:value}))}
                        onChange={value => setFormData(current => ({...current,nationality:value || ""}))}
                        filterOption={(input,option) => normalizeSearch(option.label).includes(normalizeSearch(input))}
                        placeholder="Choisir une nationalité"
                        getPopupContainer={trigger => trigger.parentElement}
                        className="w-full"
                        size="large"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Section Adresse */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-green-100 rounded-lg p-2">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Adresse</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Adresse */}
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span>Adresse</span>
                      </Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 rue de la République"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Code postal */}
                    <div className="space-y-2">
                      <Label htmlFor="postCode" className="text-sm font-medium text-gray-700">
                        Code postal
                      </Label>
                      <Input
                        id="postCode"
                        value={formData.postCode}
                        onChange={(e) => setFormData({ ...formData, postCode: e.target.value })}
                        placeholder="75001"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Ville */}
                    <div className="space-y-2">
                      <Label htmlFor="town" className="text-sm font-medium text-gray-700">
                        Ville
                      </Label>
                      <PostalCityField id="town" value={formData.town} postCode={formData.postCode} cities={postal.cities} onChange={town=>setFormData(current=>({...current,town}))} />
                      {postal.loading && <p className="text-xs text-slate-500">Recherche des communes…</p>}
                      {postal.error && <p className="text-xs text-slate-500">{postal.error}</p>}
                    </div>
                  </div>
                </motion.div>

                {/* Section Upload CV */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-orange-100 rounded-lg p-2">
                      <FileText className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Document CV</h3>
                  </div>

                  <motion.div
                    onClick={handleClick}
                    className={`relative border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all duration-300 ${
                      selectedFile
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col items-center justify-center space-y-4">
                      {selectedFile ? (
                        <>
                          <motion.div
                            className="bg-green-100 rounded-full p-3"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", duration: 0.5 }}
                          >
                            <Check className="w-8 h-8 text-green-600" />
                          </motion.div>
                          <div className="text-center">
                            <p className="text-green-800 font-medium">CV ajouté avec succès</p>
                            <p className="text-green-600 text-sm mt-1">{selectedFile.name}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <motion.div
                            className="bg-orange-100 rounded-full p-3"
                            whileHover={{ rotate: 5 }}
                          >
                            <Upload className="w-8 h-8 text-orange-600" />
                          </motion.div>
                          <div className="text-center">
                            <p className="text-gray-700 font-medium">Cliquez pour ajouter un CV</p>
                            <p className="text-gray-500 text-sm mt-1">Format PDF · 5 Mo maximum</p>
                          </div>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      ref={inputFileRef}
                      accept=".pdf"
                    />
                  </motion.div>
                  {cvAnalyzing && <p role="status" className="text-sm text-slate-600">Analyse du CV en cours… Vous pouvez continuer à remplir la fiche.</p>}
                  {!cvAnalyzing && cvAnalysisMessage && <p role="status" className="text-sm text-slate-600">{cvAnalysisMessage}</p>}
                  {selectedFile && !cvAnalyzing && <button type="button" onClick={()=>analyzeCV(selectedFile)} className="text-sm underline text-slate-600">Relancer l’analyse du CV</button>}
                </motion.div>

                {/* Section Qualifications avec LazyLoading */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-purple-100 rounded-lg p-2">
                      <Award className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Qualifications</h3>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="candidate-qualifications">Qualifications / métiers recherchés</Label>
                    <Select
                      id="candidate-qualifications"
                      aria-label="Qualifications / métiers recherchés"
                      mode="multiple"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      value={formData.qualifications}
                      options={qualifications.map(value=>({value,label:value}))}
                      onChange={values=>setFormData(current=>({...current,qualifications:values}))}
                      filterOption={(input,option)=>normalizeSearch(option.label).includes(normalizeSearch(input))}
                      placeholder="Choisir un ou plusieurs métiers"
                      getPopupContainer={trigger=>trigger.parentElement}
                      className="w-full"
                      size="large"
                    />
                  </div>
                </motion.div>

                {/* Section Informations de naissance */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-orange-100 rounded-lg p-2">
                      <Users className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Informations de naissance</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Pays de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="birthCountry" className="text-sm font-medium text-gray-700">
                        Pays de naissance
                      </Label>
                      <Select
                        id="birthCountry"
                        aria-label="Pays de naissance"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        value={formData.birthCountry || undefined}
                        options={countries.map(value=>({value,label:value}))}
                        onChange={value=>setFormData(current=>({...current,birthCountry:value || ""}))}
                        filterOption={(input,option)=>normalizeSearch(option.label).includes(normalizeSearch(input))}
                        placeholder="Choisir un pays"
                        getPopupContainer={trigger=>trigger.parentElement}
                        className="w-full"
                        size="large"
                      />
                    </div>

                    {/* Ville de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="birthTown" className="text-sm font-medium text-gray-700">
                        Ville de naissance
                      </Label>
                      <Input
                        id="birthTown"
                        value={formData.birthTown}
                        onChange={(e) => setFormData({ ...formData, birthTown: e.target.value })}
                        placeholder="Paris"
                        className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Département de naissance */}
                    <div className="space-y-2">
                      <Label htmlFor="birthDepartment" className="text-sm font-medium text-gray-700">
                        Département de naissance
                      </Label>
                      <Select
                        id="birthDepartment"
                        aria-label="Département de naissance"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        value={formData.birthDepartment || undefined}
                        options={birthDepartmentOptions}
                        onChange={value=>setFormData(current=>({...current,birthDepartment:value || ""}))}
                        filterOption={(input,option)=>normalizeSearch(option.label).includes(normalizeSearch(input))}
                        placeholder="Choisir un département ou 99"
                        getPopupContainer={trigger=>trigger.parentElement}
                        className="w-full"
                        size="large"
                      />
                    </div>
                  </div>

                  {/* Numéro de sécurité sociale */}
                  <div className="space-y-2">
                    <Label htmlFor="securityNumber" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-gray-500" />
                      <span>Numéro de sécurité sociale</span>
                    </Label>
                    <Input
                      id="securityNumber"
                      value={formData.securityNumber}
                      onChange={(e) => setFormData({ ...formData, securityNumber: e.target.value })}
                      placeholder="1 23 45 67 890 123 45"
                      className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-lg transition-all duration-200"
                    />
                  </div>

                  {/* RQTH */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="isRQTH"
                      checked={formData.isRQTH}
                      onChange={(e) => setFormData({ ...formData, isRQTH: e.target.checked })}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <Label htmlFor="isRQTH" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <Heart className="w-4 h-4 text-gray-500" />
                      <span>Reconnaissance de la Qualité de Travailleur Handicapé (RQTH)</span>
                    </Label>
                  </div>
                  {formData.isRQTH && <div className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                    <label className="col-span-full text-sm font-medium text-slate-700">Justificatif RQTH<input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={event=>setRqthFile(event.target.files?.[0] || null)} className="mt-2 block w-full text-sm"/><span className="mt-1 block text-xs font-normal text-slate-500">PDF, PNG ou JPEG · 5 Mo maximum</span></label>
                    <label className="text-sm font-medium text-slate-700">Date de début<input lang="fr-FR" type="date" value={rqthStart} onChange={event=>setRqthStart(event.target.value)} className="mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2"/></label>
                    {!rqthUnlimited && <label className="text-sm font-medium text-slate-700">Date de fin<input lang="fr-FR" type="date" min={rqthStart || undefined} value={rqthEnd} onChange={event=>setRqthEnd(event.target.value)} className="mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2"/></label>}
                    <label className="col-span-full flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={rqthUnlimited} onChange={event=>setRqthUnlimited(event.target.checked)}/>Attribuée sans limitation de durée</label>
                  </div>}
                </motion.div>

                {/* Section Contact d'urgence */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.68, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-red-100 rounded-lg p-2">
                      <Phone className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Contact d'urgence</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nom du contact d'urgence */}
                    <div className="space-y-2">
                      <Label htmlFor="urgentContactName" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Nom du contact</span>
                      </Label>
                      <Input
                        id="urgentContactName"
                        value={formData.urgentContactName}
                        onChange={(e) => setFormData({ ...formData, urgentContactName: e.target.value })}
                        placeholder="Nom Prénom"
                        className="border-gray-300 focus:border-red-500 focus:ring-red-500 rounded-lg transition-all duration-200"
                      />
                    </div>

                    {/* Téléphone du contact d'urgence */}
                    <div className="space-y-2">
                      <Label htmlFor="urgentContactPhone" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>Téléphone du contact</span>
                      </Label>
                      <Input
                        id="urgentContactPhone"
                        type="tel"
                        value={formData.urgentContactPhone}
                        onChange={(e) => setFormData({ ...formData, urgentContactPhone: e.target.value })}
                        placeholder="06 12 34 56 78"
                        className="border-gray-300 focus:border-red-500 focus:ring-red-500 rounded-lg transition-all duration-200"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Section Clients */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.3 }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-green-100 rounded-lg p-2">
                      <Building className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Clients pressentis</h3>
                  </div>

                  <ClientMultiSelect query={clientSearch} onSearch={searchClient} results={filteredClients} total={clients.length} loading={isLoadingClient} selected={formData.clients} onChange={clients=>setFormData(current=>({...current,clients}))} />
                </motion.div>

                <section className="rounded-xl border border-slate-200 p-4"><h3 className="text-lg font-semibold text-slate-900">Évaluation du candidat</h3><p className="text-sm text-slate-500">Facultatif · Note interne partagée avec l’agence.</p><CandidateStars value={formData.rating} onChange={rating=>setFormData(current=>({...current,rating}))}/></section>

                {/* Commentaire */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.85, duration: 0.3 }}
                >
                  {/* Commentaire */}
                  <div className="space-y-2">
                    <Label htmlFor="comment" className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span>Commentaire / Notes</span>
                    </Label>
                    <textarea
                      id="comment"
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      placeholder="Informations complémentaires sur le candidat..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 transition-all duration-200 resize-none"
                    />
                  </div>
                </motion.div>

                {/* Boutons d'action */}
                <motion.div
                  className="flex justify-end space-x-4 pt-6 border-t border-gray-200"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                >
                  <motion.button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Annuler
                  </motion.button>
                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                  >
                    {loading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        />
                        <span>Création...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Créer le candidat</span>
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


export default freshModal(CreateCandidateModalFormSession, "open");
